import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Stack,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AddIcon from '@mui/icons-material/Add';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableChartIcon from '@mui/icons-material/TableChart';
import Avatar from '@mui/material/Avatar';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { repairsApi } from '../api/production';
import { RepairItem, RepairType } from '../types/production';
import vendorApi from '../api/vendor';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface RepairGroup {
  design_no: string;
  design_image?: string | null;
  total_quantity: number;
  items: RepairItem[];
}

export default function RepairQueuePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const designFilter = searchParams.get('design');
  
  const [tabValue, setTabValue] = useState(0);
  const [repairItems, setRepairItems] = useState<RepairItem[]>([]);
  const [filteredRepairItems, setFilteredRepairItems] = useState<RepairItem[]>([]);
  const [repairTypes, setRepairTypes] = useState<RepairType[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [designSearchFilter, setDesignSearchFilter] = useState(designFilter || '');
  const [repairTypeFilter, setRepairTypeFilter] = useState<number | ''>('');
  
  // Grouped Details Dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<RepairGroup | null>(null);

  // Assignment dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RepairItem | null>(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [assignToVendor, setAssignToVendor] = useState(false);
  const [assignQuantity, setAssignQuantity] = useState(0);
  
  // Completion dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [repairedQty, setRepairedQty] = useState(0);
  const [permanentDamageQty, setPermanentDamageQty] = useState(0);
  const [completionNotes, setCompletionNotes] = useState('');
  const [returnToStage, setReturnToStage] = useState('DHAGA_CUTTING');
  
  // Barcode dialog
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [barcodedQty, setBarcodedQty] = useState(0);
  const [barcodePermanentDamageQty, setBarcodePermanentDamageQty] = useState(0);

  // View toggle for assigned tab
  const [assignedViewMode, setAssignedViewMode] = useState<'cards' | 'table'>('cards');

  useEffect(() => {
    fetchRepairQueue();
    fetchRepairTypes();
    fetchVendors();
  }, []);

  useEffect(() => {
    // Apply design and repair type filters
    let filtered = repairItems;
    
    if (designSearchFilter) {
      filtered = filtered.filter(item => 
        item.design_no.toLowerCase().includes(designSearchFilter.toLowerCase())
      );
    }
    
    if (repairTypeFilter !== '') {
      filtered = filtered.filter(item => 
        item.damage_type_id === repairTypeFilter
      );
    }
    
    setFilteredRepairItems(filtered);
  }, [repairItems, designSearchFilter, repairTypeFilter]);

  const getRepairGroupsByStatus = (status: string): RepairGroup[] => {
    const statusItems = filteredRepairItems.filter(i => i.repair_status === status);
    const map = new Map<string, RepairGroup>();

    statusItems.forEach((item) => {
      const existing: RepairGroup = map.get(item.design_no) || {
        design_no: item.design_no,
        design_image: (item as any).design_image || null,
        total_quantity: 0,
        items: [] as RepairItem[]
      };

      if (!existing.design_image && (item as any).design_image) {
        existing.design_image = (item as any).design_image;
      }

      existing.total_quantity += item.quantity;
      existing.items.push(item);
      map.set(item.design_no, existing);
    });

    return Array.from(map.values());
  };

  const pendingGroups = useMemo(() => getRepairGroupsByStatus('PENDING'), [filteredRepairItems]);
  const inProgressGroups = useMemo(() => getRepairGroupsByStatus('IN_PROGRESS'), [filteredRepairItems]);
  const completedGroups = useMemo(() => getRepairGroupsByStatus('COMPLETED'), [filteredRepairItems]);

  const fetchRepairQueue = async () => {
    try {
      setLoading(true);
      const data = await repairsApi.getQueue();
      setRepairItems(data);
    } catch (err: any) {
      console.error('Error fetching repair queue:', err);
      setError('Failed to load repair queue');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairTypes = async () => {
    try {
      const data = await repairsApi.getTypes();
      setRepairTypes(data);
    } catch (err) {
      console.error('Error fetching repair types:', err);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await vendorApi.getAll();
      setVendors(response.data || []);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setVendors([]);
    }
  };

  const handleAssignOpen = (item: RepairItem) => {
    setSelectedItem(item);
    setAssignedTo(item.assigned_to || '');
    setAssignQuantity(item.quantity);
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    try {
      if (!selectedItem || !assignedTo.trim()) {
        setError('Please enter worker name');
        return;
      }

      if (assignQuantity <= 0 || assignQuantity > selectedItem.quantity) {
        setError(`Quantity must be between 1 and ${selectedItem.quantity}`);
        return;
      }

      setLoading(true);
      await repairsApi.assignRepair(selectedItem.id, assignedTo, assignQuantity);
      setSuccess('Repair assigned successfully!');
      setAssignDialogOpen(false);
      fetchRepairQueue();
    } catch (err: any) {
      console.error('Error assigning repair:', err);
      setError(err.response?.data?.error || 'Failed to assign repair');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteOpen = (item: RepairItem) => {
    setSelectedItem(item);
    setRepairedQty(item.quantity);
    setPermanentDamageQty(0);
    setCompletionNotes('');
    setReturnToStage(item.return_to_stage || 'DHAGA_CUTTING');
    setCompleteDialogOpen(true);
  };

  const handleBarcodeOpen = (item: RepairItem) => {
    setSelectedItem(item);
    setBarcodedQty(0);
    setBarcodePermanentDamageQty(0);
    setBarcodeDialogOpen(true);
  };

  const handleBarcode = async () => {
    try {
      if (!selectedItem) return;

      if (barcodedQty + barcodePermanentDamageQty > selectedItem.quantity) {
        setError(`Barcoded (${barcodedQty}) + Permanent Damage (${barcodePermanentDamageQty}) = ${barcodedQty + barcodePermanentDamageQty}, but repair quantity is ${selectedItem.quantity}. Cannot exceed repair quantity.`);
        return;
      }

      if (barcodedQty + barcodePermanentDamageQty === 0) {
        setError('At least one piece must be barcoded or marked as permanent damage');
        return;
      }

      setLoading(true);
      await repairsApi.barcodeRepair(selectedItem.id, {
        barcoded_qty: barcodedQty,
        permanent_damage_qty: barcodePermanentDamageQty
      });
      
      setSuccess('Repair items barcoded successfully!');
      setBarcodeDialogOpen(false);
      fetchRepairQueue();
    } catch (err: any) {
      console.error('Error barcoding repair items:', err);
      setError(err.response?.data?.error || 'Failed to barcode repair items');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      if (!selectedItem) return;

      if (repairedQty + permanentDamageQty !== selectedItem.quantity) {
        setError(`Repaired + Permanent Damage must equal ${selectedItem.quantity}`);
        return;
      }

      setLoading(true);
      await repairsApi.completeRepair(selectedItem.id, {
        repaired_qty: repairedQty,
        permanently_damaged_qty: permanentDamageQty,
        notes: completionNotes,
        return_to_stage: returnToStage
      });
      
      setSuccess('Repair completed successfully!');
      setCompleteDialogOpen(false);
      fetchRepairQueue();
    } catch (err: any) {
      console.error('Error completing repair:', err);
      setError(err.response?.data?.error || 'Failed to complete repair');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'IN_PROGRESS': return 'info';
      case 'COMPLETED': return 'success';
      default: return 'default';
    }
  };

  const handleDetailsOpen = (group: RepairGroup) => {
    setSelectedGroup(group);
    setDetailsDialogOpen(true);
  };

  // Build vendor-wise, design-wise summary for the assigned tab table view
  const renderVendorSummaryTable = (items: RepairItem[]) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography variant="body1" color="text.secondary">Loading...</Typography>
        </Box>
      );
    }

    if (items.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">No assigned repair items found</Typography>
        </Box>
      );
    }

    // Group by vendor → design
    const vendorMap = new Map<string, Map<string, { qty: number; earliestDate: string | null; designImage?: string | null }>>();

    items.forEach((item) => {
      const vendor = item.assigned_to || 'Unassigned';
      if (!vendorMap.has(vendor)) vendorMap.set(vendor, new Map());
      const designMap = vendorMap.get(vendor)!;

      const existing = designMap.get(item.design_no) || { qty: 0, earliestDate: null as string | null, designImage: null as string | null };
      existing.qty += item.quantity;
      if (!existing.designImage && (item as any).design_image) {
        existing.designImage = (item as any).design_image;
      }
      // Track earliest date given
      const startedAt = item.repair_started_at;
      if (startedAt && (!existing.earliestDate || new Date(startedAt) < new Date(existing.earliestDate))) {
        existing.earliestDate = startedAt;
      }
      designMap.set(item.design_no, existing);
    });

    const calcAging = (dateStr: string | null): string => {
      if (!dateStr) return '-';
      const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
      if (diff === 0) return 'Today';
      if (diff === 1) return '1 day';
      return `${diff} days`;
    };

    const getAgingColor = (dateStr: string | null): string => {
      if (!dateStr) return 'inherit';
      const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
      if (diff <= 3) return theme.palette.success.main;
      if (diff <= 7) return theme.palette.warning.main;
      return theme.palette.error.main;
    };

    // Get total pieces per design across ALL in-progress items (not just current vendor)
    const designTotals = new Map<string, number>();
    items.forEach(item => {
      designTotals.set(item.design_no, (designTotals.get(item.design_no) || 0) + item.quantity);
    });

    const vendors = Array.from(vendorMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Vendor / Worker</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Design</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total (Design)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date Given</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Aging</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendors.map(([vendor, designMap]) => {
              const designs = Array.from(designMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
              const vendorTotal = designs.reduce((sum, [, d]) => sum + d.qty, 0);
              return (
                <React.Fragment key={vendor}>
                  {designs.map(([designNo, data], idx) => (
                    <TableRow key={`${vendor}-${designNo}`} hover sx={idx === 0 ? { borderTop: '2px solid', borderColor: 'divider' } : {}}>
                      {idx === 0 && (
                        <TableCell rowSpan={designs.length + 1} sx={{ verticalAlign: 'top', fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{vendor}</Typography>
                            <Typography variant="caption" color="text.secondary">{designs.length} design{designs.length !== 1 ? 's' : ''}</Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {data.designImage && (
                            <Avatar variant="rounded" src={data.designImage} sx={{ width: 32, height: 32 }} />
                          )}
                          <Typography variant="body2">{designNo}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">{data.qty}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={`${designTotals.get(designNo) || data.qty} pcs`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {data.earliestDate ? new Date(data.earliestDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold" sx={{ color: getAgingColor(data.earliestDate) }}>
                          {calcAging(data.earliestDate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Vendor subtotal row */}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={1} align="right">
                      <Typography variant="body2" fontWeight="bold">Subtotal</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">{vendorTotal}</Typography>
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </React.Fragment>
              );
            })}
            {/* Grand total */}
            <TableRow sx={{ bgcolor: 'primary.50', borderTop: '2px solid', borderColor: 'primary.main' }}>
              <TableCell colSpan={2} align="right">
                <Typography variant="body2" fontWeight="bold">Grand Total</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1" fontWeight="bold" color="primary.main">
                  {items.reduce((sum, i) => sum + i.quantity, 0)}
                </Typography>
              </TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDesignCards = (groups: RepairGroup[]) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      );
    }

    if (groups.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No repair items found
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {groups.map((group) => (
          <Grid item key={group.design_no} xs={12} sm={6} md={4} lg={3}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                boxShadow: 1,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleDetailsOpen(group)}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: isMobile ? 200 : 250,
                  bgcolor: 'background.default',
                  overflow: 'hidden'
                }}
              >
                {group.design_image ? (
                  <img
                    src={group.design_image}
                    alt={group.design_no}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                    <Avatar variant="rounded" sx={{ width: 80, height: 120 }}>
                      {group.design_no?.charAt(0) || 'R'}
                    </Avatar>
                  </Box>
                )}
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {group.design_no}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total in Repair: <strong>{group.total_quantity} pcs</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Items: <strong>{group.items.length}</strong>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Repair Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage damaged pieces and track repairs
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Pending Repairs
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {repairItems.filter(i => i.repair_status === 'PENDING').reduce((sum, i) => sum + i.quantity, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Assigned (In Progress)
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {repairItems.filter(i => i.repair_status === 'IN_PROGRESS').reduce((sum, i) => sum + i.quantity, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Pieces
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {repairItems.filter(i => i.repair_status !== 'COMPLETED').reduce((sum, i) => sum + i.quantity, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filter */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search by Design"
            variant="outlined"
            size="small"
            value={designSearchFilter}
            onChange={(e) => setDesignSearchFilter(e.target.value)}
            placeholder="Enter design number..."
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Repair Type</InputLabel>
            <Select
              value={repairTypeFilter}
              label="Filter by Repair Type"
              onChange={(e) => setRepairTypeFilter(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value="">All Repair Types</MenuItem>
              {repairTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(designSearchFilter || repairTypeFilter !== '') && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setDesignSearchFilter('');
                setRepairTypeFilter('');
              }}
            >
              Clear Filters
            </Button>
          )}
          {designFilter && (
            <Chip 
              label={`Filtered by: ${designFilter}`} 
              color="primary" 
              onDelete={() => {
                setDesignSearchFilter('');
                navigate('/production/repairs');
              }}
            />
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label={`Pending (${filteredRepairItems.filter(i => i.repair_status === 'PENDING').reduce((acc, item) => acc + item.quantity, 0)} pcs)`} />
            <Tab label={`Assigned (${filteredRepairItems.filter(i => i.repair_status === 'IN_PROGRESS').reduce((acc, item) => acc + item.quantity, 0)} pcs)`} />
            <Tab label={`Completed (${filteredRepairItems.filter(i => i.repair_status === 'COMPLETED').reduce((acc, item) => acc + item.quantity, 0)} pcs)`} />
          </Tabs>
        </Box>

        {/* Tab Panels with Cards */}
        <>
          <TabPanel value={tabValue} index={0}>
            {renderDesignCards(pendingGroups)}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {/* View toggle for Assigned tab */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <ToggleButtonGroup
                value={assignedViewMode}
                exclusive
                onChange={(_, val) => val && setAssignedViewMode(val)}
                size="small"
              >
                <ToggleButton value="cards">
                  <ViewModuleIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Cards
                </ToggleButton>
                <ToggleButton value="table">
                  <TableChartIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Summary
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {assignedViewMode === 'cards' ? (
              renderDesignCards(inProgressGroups)
            ) : (
              renderVendorSummaryTable(filteredRepairItems.filter(i => i.repair_status === 'IN_PROGRESS'))
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {renderDesignCards(completedGroups)}
          </TabPanel>
        </>
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              Repair Details - {selectedGroup?.design_no}
              <Typography variant="caption" display="block" color="text.secondary">
                Total in Repair: {selectedGroup?.total_quantity} pcs
              </Typography>
            </Box>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <AddIcon sx={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Batch</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell>Repair Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedGroup?.items
                  .filter(item => {
                    const status = tabValue === 0 ? 'PENDING' : tabValue === 1 ? 'IN_PROGRESS' : 'COMPLETED';
                    return item.repair_status === status;
                  })
                  .map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.batch?.batch_number || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.size} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.damage_type?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.repair_status}
                        color={getStatusColor(item.repair_status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {item.assigned_to || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {item.repair_status === 'PENDING' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AssignmentIndIcon />}
                              onClick={() => handleAssignOpen(item)}
                            >
                              Assign
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<QrCodeIcon />}
                              onClick={() => handleBarcodeOpen(item)}
                            >
                              Barcode
                            </Button>
                          </>
                        )}
                        {item.repair_status === 'IN_PROGRESS' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleCompleteOpen(item)}
                            >
                              Complete
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<QrCodeIcon />}
                              onClick={() => handleBarcodeOpen(item)}
                            >
                              Barcode
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Repair Work</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Design:</strong> {selectedItem?.design_no}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Size:</strong> {selectedItem?.size}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Total Quantity:</strong> {selectedItem?.quantity} pieces
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Damage:</strong> {selectedItem?.damage_type?.name}
            </Typography>

            <TextField
              label="Quantity to Assign"
              type="number"
              fullWidth
              value={assignQuantity}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 0;
                const max = selectedItem?.quantity || 0;
                setAssignQuantity(Math.max(0, Math.min(val, max)));
              }}
              InputProps={{ inputProps: { min: 1, max: selectedItem?.quantity || 0 } }}
              helperText={`Enter quantity to assign (1-${selectedItem?.quantity} pieces)`}
              sx={{ mt: 3, mb: 2 }}
            />

            <TextField
              select
              label="Assign to"
              fullWidth
              value={assignToVendor ? 'vendor' : 'worker'}
              onChange={(e) => {
                setAssignToVendor(e.target.value === 'vendor');
                setAssignedTo('');
              }}
              sx={{ mb: 2 }}
            >
              <MenuItem value="worker">Worker</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
            </TextField>

            {assignToVendor ? (
              <TextField
                select
                label="Select Vendor"
                fullWidth
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
              >
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.registered_name}>
                    {vendor.alias || vendor.registered_name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Worker Name"
                fullWidth
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                placeholder="Enter worker name"
              />
            )}

            {assignQuantity < (selectedItem?.quantity || 0) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {selectedItem && (selectedItem.quantity - assignQuantity)} pieces will remain unassigned and can be assigned separately.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={loading || !assignedTo || assignQuantity <= 0}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Repair</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Total quantity: {selectedItem?.quantity} pieces
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Permanent Damage (Cannot Repair)"
                  type="number"
                  fullWidth
                  value={permanentDamageQty}
                  onChange={(e) => {
                    const damaged = parseInt(e.target.value) || 0;
                    setPermanentDamageQty(damaged);
                    // Auto-adjust repaired qty to match total
                    const total = selectedItem?.quantity || 0;
                    const newRepaired = Math.max(0, total - damaged);
                    setRepairedQty(newRepaired);
                  }}
                  InputProps={{ inputProps: { min: 0, max: selectedItem?.quantity || 0 } }}
                  helperText="Enter damaged quantity first - repaired will auto-adjust"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Successfully Repaired (Auto-calculated)"
                  type="number"
                  fullWidth
                  value={repairedQty}
                  onChange={(e) => setRepairedQty(parseInt(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0, max: selectedItem?.quantity || 0 } }}
                  helperText="Automatically adjusted based on permanent damage"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Return repaired pieces to"
                  fullWidth
                  value={returnToStage}
                  onChange={(e) => setReturnToStage(e.target.value)}
                  helperText="Select which station repaired pieces should go to next"
                >
                  <MenuItem value="DHAGA_CUTTING">Dhaga Cutting Station</MenuItem>
                  <MenuItem value="PRESS">Press Station</MenuItem>
                  <MenuItem value="FINAL_QC">QC Station</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Any additional notes..."
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Total:</strong> {repairedQty + permanentDamageQty} / {selectedItem?.quantity}
                {repairedQty + permanentDamageQty === selectedItem?.quantity ? (
                  <Chip label="✓ Valid" color="success" size="small" sx={{ ml: 1 }} />
                ) : (
                  <Chip label="✗ Must match total" color="error" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleComplete} disabled={loading}>
            Complete Repair
          </Button>
        </DialogActions>
      </Dialog>

      {/* Barcode Dialog */}
      <Dialog open={barcodeDialogOpen} onClose={() => setBarcodeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Barcode Repair Items</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Design: <strong>{selectedItem?.design_no}</strong> | Size: <strong>{selectedItem?.size}</strong> | Total in Repair: <strong>{selectedItem?.quantity}</strong> pieces
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Barcoded Quantity"
                  type="number"
                  fullWidth
                  value={barcodedQty}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setBarcodedQty(val);
                    // Auto-adjust permanent damage to not exceed total
                    const maxPermanent = selectedItem?.quantity || 0;
                    const maxAllowed = maxPermanent - val;
                    if (barcodePermanentDamageQty > maxAllowed) {
                      setBarcodePermanentDamageQty(Math.max(0, maxAllowed));
                    }
                  }}
                  InputProps={{ inputProps: { min: 0, max: selectedItem?.quantity || 0 } }}
                  helperText="Enter number of pieces to barcode"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Permanent Damage Quantity"
                  type="number"
                  fullWidth
                  value={barcodePermanentDamageQty}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setBarcodePermanentDamageQty(val);
                    // Auto-adjust barcoded to not exceed total
                    const maxBarcoded = selectedItem?.quantity || 0;
                    const maxAllowed = maxBarcoded - val;
                    if (barcodedQty > maxAllowed) {
                      setBarcodedQty(Math.max(0, maxAllowed));
                    }
                  }}
                  InputProps={{ inputProps: { min: 0, max: selectedItem?.quantity || 0 } }}
                  helperText="Enter number of pieces to mark as permanent damage"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Total:</strong> {barcodedQty + barcodePermanentDamageQty} / {selectedItem?.quantity}
                {barcodedQty + barcodePermanentDamageQty === selectedItem?.quantity ? (
                  <Chip label="✓ All pieces processed" color="success" size="small" sx={{ ml: 1 }} />
                ) : barcodedQty + barcodePermanentDamageQty > (selectedItem?.quantity || 0) ? (
                  <Chip label="✗ Exceeds total" color="error" size="small" sx={{ ml: 1 }} />
                ) : (
                  <Chip label={`${(selectedItem?.quantity || 0) - (barcodedQty + barcodePermanentDamageQty)} remaining`} color="warning" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBarcodeDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleBarcode} 
            disabled={loading || barcodedQty + barcodePermanentDamageQty === 0 || barcodedQty + barcodePermanentDamageQty > (selectedItem?.quantity || 0)}
            startIcon={<QrCodeIcon />}
          >
            Barcode Items
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}

